/**
 * Phase 86 Plan 86-04 Task 2 — the Compost design's 196px left rail
 * (`.planning/design/mytasks-compost/README.md`, "Left rail — three
 * blocks"). Four blocks top to bottom: the sort selector, the compost heap
 * card, the thresholds legend, and (per this plan's `<design_gap_to_resolve>`
 * resolution) the four retained filter controls — client, project, tier and
 * "Show done" — that the handoff's three-block spec doesn't mention but the
 * roadmap only retires as a *layout*, not as functionality.
 *
 * The thresholds legend's day ranges are built from the SAME named
 * constants `taskRotZone` reads (`utils/format.ts`) — never restated as
 * bare literals here, so the legend can never claim a boundary the code
 * does not use.
 *
 * Phase 86 Plan 86-06 — `stacked` (README "Responsive"): below the card
 * breakpoint the rail moves above the table instead of disappearing, so the
 * sort selector and the compost counts stay reachable at phone width — the
 * handoff does not specify this, and hiding the rail would strand a narrow
 * user with no way to change sort mode. `MyTasksPage` decides WHEN to pass
 * `stacked`; this component only changes its own width/border when it does.
 */
import { FunnelIcon } from '@heroicons/react/24/outline';
import type { CompostCounts, CompostSortMode } from '../../utils/myTasksCompost';
import {
  TASK_WARM_THRESHOLD_DAYS,
  TASK_STALE_THRESHOLD_DAYS,
  TASK_GRAVEYARD_THRESHOLD_DAYS,
  getTierColor,
  chipTextColor,
} from '../../utils/format';
import {
  FRESH_HEX,
  STALE_DARK_HEX,
  GRAVEYARD_DARK_HEX,
  MICRO_LABEL_GREY_HEX,
  DASHED_BORDER_GREY_HEX,
  ACCENT_TINT_HEX,
} from './compostTokens';
import type { FilterCustomer, FilterProject, TierOption } from './filterTypes';

const SECTION_LABEL =
  'font-mono text-[9px] font-bold uppercase tracking-[0.18em]';

const SORT_ROWS: Array<{ mode: CompostSortMode; label: string }> = [
  { mode: 'rot', label: 'Rot' },
  { mode: 'pressure', label: 'Pressure / SLA' },
  { mode: 'due', label: 'Due date' },
  { mode: 'project', label: 'Project' },
];

const THRESHOLD_LEGEND_ROWS: Array<{ range: string; label: string; colorHex: string }> = [
  { range: `0–${TASK_WARM_THRESHOLD_DAYS}d`, label: 'fresh', colorHex: FRESH_HEX },
  {
    range: `${TASK_WARM_THRESHOLD_DAYS + 1}–${TASK_STALE_THRESHOLD_DAYS}d`,
    label: 'warm',
    colorHex: MICRO_LABEL_GREY_HEX,
  },
  { range: `>${TASK_STALE_THRESHOLD_DAYS}d`, label: '💩 stale', colorHex: STALE_DARK_HEX },
  {
    range: `>${TASK_GRAVEYARD_THRESHOLD_DAYS}d`,
    label: '🪦 abandoned',
    colorHex: GRAVEYARD_DARK_HEX,
  },
];

function compostFooterCopy(counts: CompostCounts): string {
  const n = counts.staleCount + counts.graveyardCount;
  if (n === 0) return 'Nothing in the heap right now.';
  if (n === 1) return 'One task is quietly rotting.';
  return `${n} tasks are quietly rotting.`;
}

export interface MyTasksRailProps {
  sortMode: CompostSortMode;
  onSortModeChange: (mode: CompostSortMode) => void;
  /** Per-row count shown in the sort selector (project's is a project count, not a task count). */
  sortCounts: Record<CompostSortMode, number>;
  /** The SAME counts the header sentence is generated from — never restated independently. */
  compostCounts: CompostCounts;

  customers: FilterCustomer[];
  customerFilter: number | null;
  onCustomerFilterChange: (next: number | null) => void;
  projectFilter: number | null;
  onProjectFilterChange: (next: number | null) => void;
  availableTiers: TierOption[];
  tierFilter: number[];
  onTierFilterChange: (next: number[]) => void;
  showDone: boolean;
  onShowDoneChange: (next: boolean) => void;
  /** Phase 86-06 — true below the card breakpoint: the rail renders full-
   * width, above the table, instead of as a fixed 196px sidebar. */
  stacked?: boolean;
}

function TierFilterList({
  availableTiers,
  tierFilter,
  onTierFilterChange,
}: Pick<MyTasksRailProps, 'availableTiers' | 'tierFilter' | 'onTierFilterChange'>) {
  if (availableTiers.length === 0) return null;

  function toggle(id: number) {
    const next = tierFilter.includes(id)
      ? tierFilter.filter((t) => t !== id)
      : [...tierFilter, id];
    onTierFilterChange(next);
  }

  return (
    <div className="flex flex-col gap-1" data-testid="my-tasks-rail-tier-filter">
      {availableTiers.map((tier) => {
        const selected = tierFilter.includes(tier.id);
        const bg = getTierColor(tier.orderIndex);
        const fg = chipTextColor(bg);
        return (
          <button
            key={tier.id}
            type="button"
            aria-pressed={selected}
            data-testid={`my-tasks-rail-tier-${tier.id}`}
            onClick={() => toggle(tier.id)}
            className={`flex items-center gap-1.5 px-1.5 py-1 rounded text-[10.5px] text-left ${
              selected ? 'bg-ms-surface-2' : ''
            }`}
          >
            <span
              className="inline-block px-1 py-0.5 rounded text-[9px] font-semibold shrink-0"
              style={{ background: bg, color: fg }}
            >
              P{tier.orderIndex}
            </span>
            <span className="truncate" style={{ color: MICRO_LABEL_GREY_HEX }}>
              {tier.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function MyTasksRail({
  sortMode,
  onSortModeChange,
  sortCounts,
  compostCounts,
  customers,
  customerFilter,
  onCustomerFilterChange,
  projectFilter,
  onProjectFilterChange,
  availableTiers,
  tierFilter,
  onTierFilterChange,
  showDone,
  onShowDoneChange,
  stacked = false,
}: MyTasksRailProps) {
  const activeCustomer = customerFilter
    ? customers.find((c) => c.id === customerFilter) ?? null
    : null;
  const visibleProjects: FilterProject[] = activeCustomer
    ? activeCustomer.projects
    : customers.flatMap((c) => c.projects);

  return (
    <aside
      data-testid="my-tasks-rail"
      data-stacked={stacked ? 'true' : 'false'}
      className={`${
        stacked ? 'w-full border-b' : 'w-[196px] shrink-0 border-r'
      } border-ms-border bg-ms-surface py-4 overflow-y-auto`}
    >
      {/* Block 1 — SORT BY */}
      <div className={`${SECTION_LABEL} px-3.5 pb-2`} style={{ color: MICRO_LABEL_GREY_HEX }}>
        SORT BY
      </div>
      <div className="flex flex-col" role="group" aria-label="Sort my tasks by">
        {SORT_ROWS.map((row) => {
          const active = row.mode === sortMode;
          return (
            <button
              key={row.mode}
              type="button"
              aria-pressed={active}
              data-testid={`my-tasks-sort-${row.mode}`}
              onClick={() => onSortModeChange(row.mode)}
              className="flex items-center gap-2 px-3.5 py-[7px] border-l-2 text-left"
              style={{
                borderLeftColor: active ? '#FC8332' : 'transparent',
                background: active ? ACCENT_TINT_HEX : undefined,
              }}
            >
              <span
                className={`flex-1 text-[11.5px] ${active ? 'font-semibold' : 'font-normal'}`}
                style={{ color: active ? undefined : 'var(--ms-text-secondary)' }}
              >
                {row.label}
              </span>
              <span
                className="font-mono text-[10px]"
                style={{ color: active ? '#FC8332' : MICRO_LABEL_GREY_HEX }}
              >
                {sortCounts[row.mode]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Block 2 — COMPOST HEAP */}
      <div
        className="mx-3.5 mt-[18px] p-3 rounded-[6px] border border-ms-border bg-ms-canvas"
        data-testid="my-tasks-compost-card"
      >
        <div className={`${SECTION_LABEL} mb-[9px]`} style={{ color: MICRO_LABEL_GREY_HEX }}>
          COMPOST HEAP
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 15, lineHeight: 1 }} aria-hidden="true">
              💩
            </span>
            <span
              className="font-mono text-[15px] font-bold"
              style={{ color: STALE_DARK_HEX }}
              data-testid="my-tasks-compost-stale-count"
            >
              {compostCounts.staleCount}
            </span>
            <span className="text-[10.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
              going stale
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span style={{ fontSize: 15, lineHeight: 1 }} aria-hidden="true">
              🪦
            </span>
            <span
              className="font-mono text-[15px] font-bold"
              style={{ color: GRAVEYARD_DARK_HEX }}
              data-testid="my-tasks-compost-graveyard-count"
            >
              {compostCounts.graveyardCount}
            </span>
            <span className="text-[10.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
              abandoned
            </span>
          </div>
        </div>
        <div
          className="mt-2.5 pt-[9px] text-[10.5px] leading-[1.5]"
          style={{ borderTop: `1px dashed ${DASHED_BORDER_GREY_HEX}`, color: MICRO_LABEL_GREY_HEX }}
        >
          {compostFooterCopy(compostCounts)}
        </div>
      </div>

      {/* Block 3 — THRESHOLDS */}
      <div className="mx-3.5 mt-4 pt-3.5 border-t border-ms-border">
        <div className={`${SECTION_LABEL} mb-2`} style={{ color: MICRO_LABEL_GREY_HEX }}>
          THRESHOLDS
        </div>
        <div className="flex flex-col gap-[7px] text-[10.5px]" style={{ color: MICRO_LABEL_GREY_HEX }}>
          {THRESHOLD_LEGEND_ROWS.map((row) => (
            <div key={row.label} className="flex items-center gap-[7px]">
              <span
                className="w-[34px] font-mono text-[9.5px] font-bold shrink-0"
                style={{ color: row.colorHex }}
              >
                {row.range}
              </span>
              <span>{row.label}</span>
            </div>
          ))}
        </div>
        <div className="mt-2.5 text-[10px] leading-[1.5]" style={{ color: MICRO_LABEL_GREY_HEX }}>
          Clock = last activity: edit, comment, or time entry. Done and archived never rot.
        </div>
      </div>

      {/* Block 4 — retained filters (client / project / tier / show done),
          per this plan's design_gap_to_resolve resolution. */}
      <div className="mx-3.5 mt-4 pt-3.5 border-t border-ms-border">
        <div className={`${SECTION_LABEL} mb-2`} style={{ color: MICRO_LABEL_GREY_HEX }}>
          FILTERS
        </div>

        <label className="block mb-2" data-testid="my-tasks-rail-filter-customer">
          <span className="sr-only">Filter by client</span>
          <select
            aria-label="Filter by client"
            value={customerFilter ?? ''}
            onChange={(e) => {
              const next = e.target.value === '' ? null : Number(e.target.value);
              onCustomerFilterChange(next);
              if (next !== customerFilter) onProjectFilterChange(null);
            }}
            className="w-full px-2 py-1.5 rounded border border-ms-border bg-ms-canvas text-[11px]"
          >
            <option value="">All clients</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block mb-3" data-testid="my-tasks-rail-filter-project">
          <span className="sr-only">Filter by project</span>
          <select
            aria-label="Filter by project"
            value={projectFilter ?? ''}
            onChange={(e) =>
              onProjectFilterChange(e.target.value === '' ? null : Number(e.target.value))
            }
            className="w-full px-2 py-1.5 rounded border border-ms-border bg-ms-canvas text-[11px]"
          >
            <option value="">All projects</option>
            {visibleProjects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        {availableTiers.length > 0 && (
          <div className="mb-3">
            <div className="text-[9.5px] font-semibold uppercase tracking-wider mb-1" style={{ color: MICRO_LABEL_GREY_HEX }}>
              <FunnelIcon className="h-3 w-3 inline-block mr-1" aria-hidden="true" />
              Tier
            </div>
            <TierFilterList
              availableTiers={availableTiers}
              tierFilter={tierFilter}
              onTierFilterChange={onTierFilterChange}
            />
          </div>
        )}

        <button
          type="button"
          onClick={() => onShowDoneChange(!showDone)}
          aria-pressed={showDone}
          data-testid="my-tasks-rail-filter-done"
          className="w-full px-2 py-1.5 rounded border text-[11px] text-left"
          style={{
            borderColor: showDone ? '#FC8332' : 'var(--ms-border)',
            background: showDone ? ACCENT_TINT_HEX : undefined,
            color: showDone ? '#FC8332' : MICRO_LABEL_GREY_HEX,
          }}
        >
          {showDone ? '✓ Show done' : 'Show done'}
        </button>
      </div>
    </aside>
  );
}
